import { formatDate, formatUTCDate, formatDecimalPlace, formatNumber } from "common/formatters";
import Offer from "domain/offer";
import { getOfferTypeName, OfferTypeEnum } from "domain/offerType";
import React from "react";
import { getPaymentOptionDisplayName } from "domain/paymentOption";
import { getProducerDisplayName } from "features/settings/producers/producer";
import { toYesNo } from "common/formatters";
import { getOrderTypeName, OrderType } from "domain/orderType";
import { getTraderDisplayName } from "features/settings/traders/trader";
import { getDeliveryPeriodDisplayName } from "domain/deliveryPeriod";
import ErrorList from "common/ui/ErrorList";
import { formatCustomerWillSell, getPriceLabelForOffer } from "common/formatters/strings";
import { useSelector } from "react-redux";
import IAppStore from "app/IAppStore";
import { getFinalPrice, getTotalFees } from "./helpers";
import { getProducerAccountDisplayName } from "features/settings/producers/producerAccount";
import offerTypeHelper from "domain/offerTypeHelper";
import { getTasFallbackOptionLabel } from "domain/tasFallbackOption";
import unitHelper from "domain/unitHelper";
import useTranslators from "common/translation/useTranslators";
import useUnitConversionRates from "features/settings/commodities/useUnitConversionRates";
import { usePaymentOptions } from 'features/merchants/usePaymentOptions';
import { localizeOfferContract as localize, localizeCommon } from "features/localization/localizeService";

export type OfferDetailsConfirmationProps = {
    offer: Offer;
    errors: string[];
    showPaymentOption: boolean;
    showHedgeSettings: boolean;
};

interface Prices {
    price: string;
    finalPrice: string;
}

function getPrices(props: OfferDetailsConfirmationProps): Prices {
    if (props.offer.orderType === OrderType.Market && props.offer.type !== OfferTypeEnum.FixBasisOnHta && !props.offer.buyAtTarget && !props.offer.isPriceLocked)
    {
        const priceStr = props.offer.isPriceAtOpenEnabled ? localize("Details_Label_PriceAtOpen") : localize("Details_Label_AtMarket");
        return { price: priceStr, finalPrice: priceStr };
    }   
    return { 
        price: formatDecimalPlace(props.offer.cashPrice), 
        finalPrice: getFinalPrice(props.offer).toString() 
    };
}

const OfferDetailsConfirmation: React.FC<OfferDetailsConfirmationProps> = (props: OfferDetailsConfirmationProps) => {
    
    const isCustomer = useSelector((m: IAppStore) => m.userState.isCustomer);
    const activeMerchant = useSelector((m: IAppStore) => m.userState.activeMerchant);
    const totalFees: number = getTotalFees(props.offer);
    const prices: Prices = getPrices(props);
    const unitsConversionRates = useUnitConversionRates();
    const paymentOptionsDetails = usePaymentOptions();
    const showLocalUOM = props.offer?.commodity?.baseUnit.id !== props.offer?.commodity?.localUnit.id;
    const translators = useTranslators();

    function getPriceText(price: number) {
        return unitHelper.getPriceText(price, showLocalUOM, props.offer.currency.code, props.offer.commodity, unitsConversionRates);
    }

    return <>
        <table className="table table-striped offer-confirmation">
            <tbody>
                <tr className="highlight">
                    <td>{localize("Details_Label_Type")}</td>
                    <td className="offer-value">{getOfferTypeName(props.offer.type)}</td>
                </tr>
                {!isCustomer && <tr>
                    <td>{localize("Details_Label_Customer")}</td>
                    <td className="offer-value">{getProducerDisplayName(props.offer.producer)} </td>
                </tr>}
                <tr>
                    <td>{localize("Details_Label_AccountId")}</td>
                    <td className="offer-value">{getProducerAccountDisplayName(props.offer.customerAccount)}</td>
                </tr>
                <tr className="highlight">
                    <td>{localize("Details_Label_CustomerWill")}</td>
                    <td className="offer-value">{formatCustomerWillSell(props.offer.customerWillSell)}</td>
                </tr>
                <tr>
                    <td>{localize("Details_Label_Commodity")}</td>
                    <td className="offer-value">{props.offer.commodity.name}</td>
                </tr>
                {activeMerchant.displayContractLocation && <tr>
                    <td>{localize("Details_Label_ContractLocation")}</td>
                    <td className="offer-value">{props.offer.contractLocation ? props.offer.contractLocation.name : localizeCommon("Value_DetermineLater")}</td>
                </tr>}
                <tr>
                    <td>{localize("Details_Label_Location")}</td>
                    <td className="offer-value">{props.offer.location ? props.offer.location.name : localizeCommon("Value_DetermineLater")}</td>
                </tr>
                {activeMerchant.displayRegionOnAddOffer && props.offer.region != null && <tr>
                    <td>{localize("Details_Label_Region")}</td>
                    <td className="offer-value">{props.offer.region.name}</td>
                </tr>}
                {props.offer.deliveryMethod != null && <tr>
                    <td>{localize("Details_Label_DeliveryMethod")}</td>
                    <td className="offer-value">{translators.deliveryMethods.translate(props.offer.deliveryMethod)}</td>
                </tr>}
                {props.offer.delayedContract && <tr>
                    <td>{localize("Details_Label_DeliveryPeriodContract")}</td>
                    <td className="offer-value">{props.offer.delayedContract.displayId} </td>
                </tr>}
                {Boolean(props.offer.freightFee) && <tr>
                    <td>{localize("Details_Label_FreightFee")}</td>
                    <td className="offer-value">{formatDecimalPlace(props.offer.freightFee)}</td>
                </tr>}
                {props.offer.type !== OfferTypeEnum.DelayedPriceContract && <><tr>
                    <td>{localize("Details_Label_DeliveryPeriod")}</td>
                    <td className="offer-value">{getDeliveryPeriodDisplayName(props.offer.deliveryPeriod)} </td>
                </tr>
                <tr>
                    <td>{localize("Details_Label_StartDate")}</td>
                    <td className="offer-value">{formatUTCDate(props.offer.deliveryPeriodStartDate)} </td>
                </tr>
                <tr>
                    <td>{localize("Details_Label_EndDate")}</td>
                    <td className="offer-value">{formatUTCDate(props.offer.deliveryPeriodEndDate)} </td>
                </tr></>}
                {props.offer.htaFee != null && <tr>
                    <td>{localize("Details_Label_HtaFee")}</td>
                    <td className="offer-value">{formatDecimalPlace(props.offer.htaFee)} </td>
                </tr>}
                {props.offer.fees.length > 0 && <tr>
                    <td>{localize("Details_Label_TotalFees")}</td>
                    <td className="offer-value">{getPriceText(totalFees)}</td>
                </tr>}
                {props.offer.futuresContract && <tr>
                    <td>{localize("Details_Label_Symbol")}</td>
                    <td className="offer-value">{props.offer.futuresContract.symbol}</td>
                </tr>}
                {offerTypeHelper.hasBasis(props.offer.type) && <tr>
                    <td>{localize("Details_Label_Basis")}</td>
                    <td className="offer-value">{props.offer.basis == null ? "--" : formatDecimalPlace(props.offer.basis)}</td>
                </tr>}
                {offerTypeHelper.canBeHedged(props.offer.type) && props.offer.orderType && <tr className="highlight">
                    <td>{localize("Details_Label_OrderType")}</td>
                    <td className="offer-value">{getOrderTypeName(props.offer.orderType)}</td>
                </tr>}
                {Boolean(props.offer.cashPrice) && <><tr className="highlight">
                    <td>{getPriceLabelForOffer(props.offer.type, props.offer.orderType)}</td>
                    <td className="offer-value">{Number.isNaN(Number.parseFloat(prices.price)) || props.offer.orderType === OrderType.Tas ? prices.price : getPriceText(Number(prices.price))}</td>
                </tr>
                {props.offer.orderType !== OrderType.Tas &&
                <tr>
                    <td>{localize("Details_Label_FinalPrice")}</td>
                    <td className="offer-value">{Number.isNaN(Number.parseFloat(prices.finalPrice)) ? prices.finalPrice : getPriceText(Number(prices.finalPrice))}</td>
                </tr>}</>}
                <tr className="highlight">
                    <td>{localize("Details_Label_Quantity")}</td>
                    <td className="offer-value">{showLocalUOM ? unitHelper.getQuantityTextWithUnits(props.offer.quantity, props.offer.commodity, unitsConversionRates)
                    : `${formatNumber(props.offer.quantity, '0,0.[00]')} ${props.offer.commodity.baseUnit.abbreviation}`}</td>
                </tr>
                {props.offer.orderType !== OrderType.Tas && (props.offer.orderType !== OrderType.Market || [OfferTypeEnum.BasisContract, OfferTypeEnum.DelayedPriceContract].includes(props.offer.type)) &&
                <tr>
                    <td>{[OfferTypeEnum.BasisContract, OfferTypeEnum.DelayedPriceContract].includes(props.offer.type) ? localize("Details_Label_FinalPricingDate") : localize("Details_Label_Expiration")}</td>
                    <td className="offer-value">{formatUTCDate(props.offer.expirationDate)}</td>
                </tr>
                }
                {props.offer.orderType === OrderType.Tas && (
                    <tr>
                        <td>{localize("Details_Label_TasFallbackOption")}</td>
                        <td>{getTasFallbackOptionLabel(props.offer.tasFallbackOption)}</td>
                    </tr>
                )}
                {!isCustomer && <tr>
                    <td>{localize("Details_Label_Trader")}</td>
                    <td className="offer-value">{getTraderDisplayName(props.offer.trader)}</td>
                </tr>}
                {props.showPaymentOption && <tr>
                    <td>{localize("Details_Label_PaymentOption")}</td>
                    <td className="offer-value">{getPaymentOptionDisplayName(props.offer.paymentOption, paymentOptionsDetails)}</td>
                </tr>}
                {props.offer.deferredPayDate && <tr>
                    <td>{localize("Details_Label_PayDate")}</td>
                    <td className="offer-value">{formatDate(props.offer.deferredPayDate)}</td>
                </tr>}
                {props.offer.interestStartDate && <tr>
                    <td>{localize("Details_Label_InterestStartDate")}</td>
                    <td className="offer-value">{formatDate(props.offer.interestStartDate)}</td>
                </tr>}
                {props.offer.interestRate != null && <tr>
                    <td>{localize("Details_Label_InterestRate")}</td>
                    <td className="offer-value">{props.offer.interestRate}%</td>
                </tr>}
                {props.showHedgeSettings && <>
                <tr>
                    <td>{localize("Details_Label_FcmAccount")}</td>
                    <td className="offer-value">{props.offer.fcmAccount?.accountNumber}</td>
                </tr>
                <tr>
                    <td>{localize("Details_Label_UseMinis")}?</td>
                        <td className="offer-value">{toYesNo(props.offer.useMini ?? false)}</td>
                </tr>
                <tr>
                    <td>{localize("Details_Label_Contracts")}</td>
                    <td className="offer-value">{props.offer.numberOfContracts}</td>
                </tr></>}
                {props.offer.deliveryTerms && <tr>
                    <td>{localize("Details_Label_DeliveryTerms")}</td>
                    <td className="offer-value">{props.offer.deliveryTerms}</td>
                </tr>}
                {props.offer.customerContractId && <tr>
                    <td>{localize("Details_Label_CustomerContractId")}</td>
                    <td className="offer-value">{props.offer.customerContractId}</td>
                </tr>}
            </tbody>
        </table>
        {props.offer.comments && <>
            <h5>{localize("Details_Label_Comments")}</h5>
            <p>{props.offer.comments}</p>
        </>}
        {isCustomer && <div className="terms">
            <h5 className="terms-heading">{localize("Details_Label_TermsAndConditions")}</h5>
            <p>{localize("Details_Label_TermsConfirmationInformation")}.</p>
        </div>}

        <ErrorList errors={props.errors}>
            <h3>{localize("Details_Label_Errors")}</h3>
        </ErrorList>
    </>
};

export default OfferDetailsConfirmation;